import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api, Video, PolicyCheckResult, PolicyCheckDetail } from '../lib/api'
import HudLoader from '../components/HudLoader'
import {
  ShieldCheck,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Info,
  Loader2,
  RefreshCw,
  Filter,
  Search,
  Eye,
} from 'lucide-react'
import { format } from 'date-fns'

const SEVERITY_COLORS: Record<string, string> = {
  blocker: 'badge-danger',
  error: 'badge-danger',
  warning: 'badge-warning',
  info: 'badge-info',
}

const SEVERITY_ICONS: Record<string, React.ComponentType<any>> = {
  blocker: XCircle,
  error: AlertTriangle,
  warning: AlertTriangle,
  info: Info,
}

export default function Policies() {
  const queryClient = useQueryClient()
  const [selectedVideo, setSelectedVideo] = useState<string | null>(null)
  const [policyResult, setPolicyResult] = useState<PolicyCheckResult | null>(null)
  const [autoFix, setAutoFix] = useState(false)

  const { data: videos, isLoading } = useQuery({
    queryKey: ['videos', 'all'],
    queryFn: () => api.get<Video[]>('/videos?limit=100'),
  })

  const checkMutation = useMutation({
    mutationFn: ({ videoId, checkTypes, autoFix }: { videoId: string; checkTypes?: string[]; autoFix: boolean }) =>
      api.post<PolicyCheckResult>('/policies/check', { video_id: videoId, check_types: checkTypes, auto_fix: autoFix }),
    onSuccess: (data) => {
      setPolicyResult(data)
      setSelectedVideo(data.video_id)
    },
  })

  const runAllMutation = useMutation({
    mutationFn: (videoId: string) => api.post<PolicyCheckResult>(`/policies/${videoId}/run-all`, { auto_fix: autoFix }),
    onSuccess: (data) => {
      setPolicyResult(data)
      setSelectedVideo(data.video_id)
    },
  })

  if (isLoading) return <HudLoader text="Loading videos" />

  const getSeverityBadge = (severity: string) => (
    <span className={`badge ${SEVERITY_COLORS[severity] || 'badge-secondary'}`}>
      {severity}
    </span>
  )

  const getSeverityIcon = (severity: string) => {
    const Icon = SEVERITY_ICONS[severity] || Info
    return <Icon size={14} />
  }

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Policy Compliance</h1>
          <p>Monitor monetization eligibility and policy compliance for all videos</p>
        </div>
      </div>

      <div className="grid grid-2">
        <div className="card">
          <h2>Video Policy Status</h2>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Video</th>
                  <th>Status</th>
                  <th>Language</th>
                  <th>Last Check</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {videos?.map((video) => (
                  <tr key={video.id}>
                    <td>
                      <span className="video-title">{video.title}</span>
                    </td>
                    <td>
                      <span className={`badge ${video.status === 'compliance_passed' ? 'badge-success' : video.status === 'compliance_failed' ? 'badge-danger' : 'badge-secondary'}`}>
                        {video.status.replace('_', ' ')}
                      </span>
                    </td>
                    <td>{video.default_language}</td>
                    <td>
                      {video.policy_checks?.length > 0 ? (
                        format(new Date(video.policy_checks[0].created_at), 'MMM dd, HH:mm')
                      ) : '-'}
                    </td>
                    <td>
                      <div className="action-menu">
                        <button
                          className="icon-btn"
                          onClick={() => runAllMutation.mutate(video.id)}
                          disabled={runAllMutation.isPending}
                          title="Run All Checks"
                        >
                          {runAllMutation.isPending && runAllMutation.variables === video.id ? <Loader2 size={16} className="spin" /> : <ShieldCheck size={16} />}
                        </button>
                        <button className="icon-btn" onClick={() => setSelectedVideo(video.id)} title="View Details">
                          <Eye size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="card">
          <h2>Compliance Details</h2>
          {selectedVideo && policyResult ? (
            <div className="policy-results">
              <div className="policy-summary">
                <div className={`summary-status ${policyResult.overall_passed ? 'passed' : 'failed'}`}>
                  <span className="status-icon">{policyResult.overall_passed ? <CheckCircle size={24} /> : <XCircle size={24} />}</span>
                  <div>
                    <h3>{policyResult.overall_passed ? 'All Checks Passed' : 'Policy Issues Found'}</h3>
                    <p>{policyResult.overall_passed ? 'Video is ready for monetization' : 'Review issues before publishing'}</p>
                  </div>
                </div>
                <div className="summary-counts">
                  <div className="count-item danger">{policyResult.blocker_count} <span>Blockers</span></div>
                  <div className="count-item error">{policyResult.error_count} <span>Errors</span></div>
                  <div className="count-item warning">{policyResult.warning_count} <span>Warnings</span></div>
                  <div className="count-item info">{policyResult.info_count} <span>Info</span></div>
                </div>
              </div>

              <div className="checks-list">
                {policyResult.checks.map((check: PolicyCheckDetail) => (
                  <div key={check.check_type} className={`check-item ${check.severity}`}>
                    <div className="check-header">
                      <div className="check-type">
                        {getSeverityIcon(check.severity)}
                        <span>{check.check_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
                        {getSeverityBadge(check.severity)}
                      </div>
                      <div className="check-status">
                        {check.passed ? <CheckCircle size={16} className="passed" /> : <XCircle size={16} className="failed" />}
                      </div>
                    </div>
                    <p className="check-message">{check.message}</p>
                    {check.auto_fixable && (
                      <span className="auto-fix-badge">Auto-fixable</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="empty-state">
              <ShieldCheck size={48} />
              <h3>Select a video</h3>
              <p>Run policy checks on a video to see detailed compliance results</p>
            </div>
          )}
        </div>
      </div>

      <div className="card mt-4">
        <h2>Policy Check Types</h2>
        <div className="policy-types">
          <div className="policy-type">
            <ShieldCheck size={20} />
            <div>
              <h4>YPP Eligibility</h4>
              <p>Checks channel meets YouTube Partner Program requirements (1000 subs, 4000 watch hours)</p>
            </div>
          </div>
          <div className="policy-type">
            <AlertTriangle size={20} />
            <div>
              <h4>Reused Content</h4>
              <p>Detects low-originality content like compilations, top 10s without commentary</p>
            </div>
          </div>
          <div className="policy-type">
            <AlertTriangle size={20} />
            <div>
              <h4>Advertiser Friendly</h4>
              <p>Scans for nudity, profanity, violence, drugs, controversial topics, sensitive events</p>
            </div>
          </div>
          <div className="policy-type">
            <AlertTriangle size={20} />
            <div>
              <h4>Copyright Match</h4>
              <p>Identifies potential copyright risk from unlicensed music, clips, or content</p>
            </div>
          </div>
          <div className="policy-type">
            <Info size={20} />
            <div>
              <h4>AI Disclosure</h4>
              <p>Ensures AI-generated content is properly disclosed per YouTube policy</p>
            </div>
          </div>
          <div className="policy-type">
            <AlertTriangle size={20} />
            <div>
              <h4>Spam & Deceptive</h4>
              <p>Detects misleading metadata, clickbait, spam tags, and deceptive practices</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
